import { create } from 'zustand';
import { devtools } from 'zustand/middleware';

const ROSETTA_INITIAL_STATE = {
  loading: false,
  tradesData: [],
  companyName: '',
  customScopes: {},
  showPricing: false,
  currentImages: [],
  manualDiscount: 0,
  selectedScopes: {},
  projectsPerYear: 10,
  showResetModal: false,
  showImageModal: false,
  sheetsPerProject: 10,
  currentImageIndex: 0,
  totalAnnualSheets: 100,
  selectedTrades: new Set(),
  expandedTrades: new Set(),
  currentDetailItem: null,
  inflationPercentage: 0,
  volumeDiscountTiers: {},
  perTradeDistribution: {},
  manualDiscountEnabled: false,
  scopeMode: false,
  showTiersTable: true,
  hiddenTiers: new Set()
};

const reducer = (state, { type, payload }) => {
  switch (type) {
    case 'SET_LOADING':
      return { ...state, loading: payload };
    case 'SET_TRADES_DATA':
      return { ...state, tradesData: payload };
    case 'SET_COMPANY_NAME':
      return { ...state, companyName: payload };
    case 'SET_CUSTOM_SCOPES':
      return { ...state, customScopes: payload };
    case 'SET_SHOW_PRICING':
      return { ...state, showPricing: payload };
    case 'SET_CURRENT_IMAGES':
      return { ...state, currentImages: payload };
    case 'SET_MANUAL_DISCOUNT':
      return { ...state, manualDiscount: payload };
    case 'SET_SELECTED_SCOPES':
      return { ...state, selectedScopes: payload };
    case 'SET_PROJECTS_PER_YEAR':
      return { ...state, projectsPerYear: payload };
    case 'SET_SHOW_RESET_MODAL':
      return { ...state, showResetModal: payload };
    case 'SET_SHOW_IMAGE_MODAL':
      return { ...state, showImageModal: payload };
    case 'SET_SHEETS_PER_PROJECT':
      return { ...state, sheetsPerProject: payload };
    case 'SET_CURRENT_IMAGE_INDEX':
      return { ...state, currentImageIndex: payload };
    case 'SET_TOTAL_ANNUAL_SHEETS':
      return { ...state, totalAnnualSheets: payload };
    case 'SET_SELECTED_TRADES':
      return { ...state, selectedTrades: payload };
    case 'SET_EXPANDED_TRADES':
      return { ...state, expandedTrades: payload };
    case 'SET_CURRENT_DETAIL_ITEM':
      return { ...state, currentDetailItem: payload };
    case 'SET_INFLATION_PERCENTAGE':
      return { ...state, inflationPercentage: payload };
    case 'SET_VOLUME_DISCOUNT_TIERS':
      return { ...state, volumeDiscountTiers: payload };
    case 'SET_PER_TRADE_DISTRIBUTION':
      return { ...state, perTradeDistribution: payload };
    case 'SET_MANUAL_DISCOUNT_ENABLED':
      return { ...state, manualDiscountEnabled: payload };
    case 'SET_SCOPE_MODE':
      return { ...state, scopeMode: payload };
    case 'SET_SHOW_TIERS_TABLE':
      return { ...state, showTiersTable: payload };
    case 'TOGGLE_TIER_VISIBILITY':
      const newHiddenTiers = new Set(state.hiddenTiers);
      const tierKey = typeof payload === 'string' ? parseInt(payload, 10) : payload;
      if (newHiddenTiers.has(tierKey)) {
        newHiddenTiers.delete(tierKey);
      } else {
        newHiddenTiers.add(tierKey);
      }
      return { ...state, hiddenTiers: newHiddenTiers };
    case 'RESET_ROSETTA_STATE':
      return { ...state, ...ROSETTA_INITIAL_STATE };
    case 'RESET_CONFIGURATION':
      // Get default selected scopes from trades data
      const defaultSelectedScopes = {};
      if (state.tradesData && state.tradesData.length > 0) {
        state.tradesData.forEach(trade => {
          if (trade.scopes && Array.isArray(trade.scopes)) {
            defaultSelectedScopes[trade.trade_id] = new Set(
              trade.scopes.filter(scope => scope.is_default_selected).map(scope => scope.scope_id)
            );
          }
        });
      }

      return {
        ...state,
        selectedTrades: new Set(),
        expandedTrades: new Set(),
        selectedScopes: defaultSelectedScopes,
        currentDetailItem: null,
        currentImages: [],
        currentImageIndex: 0,
        companyName: '',
        projectsPerYear: 10,
        sheetsPerProject: 10,
        totalAnnualSheets: 100,
        perTradeDistribution: {},
        manualDiscount: 0,
        manualDiscountEnabled: false,
        showPricing: false,
        showResetModal: false,
        customScopes: {}
      };
    case 'UPDATE_CUSTOM_SCOPE':
      return {
        ...state,
        customScopes: {
          ...state.customScopes,
          [payload.tradeId]: payload.value
        }
      };
    case 'TOGGLE_TRADE_SELECTION':
      const newSelectedTrades = new Set(state.selectedTrades);
      if (newSelectedTrades.has(payload)) {
        newSelectedTrades.delete(payload);
      } else {
        newSelectedTrades.add(payload);
      }
      return { ...state, selectedTrades: newSelectedTrades };
    case 'TOGGLE_TRADE_DROPDOWN':
      const newExpandedTrades = new Set(state.expandedTrades);
      if (newExpandedTrades.has(payload)) {
        newExpandedTrades.delete(payload);
      } else {
        newExpandedTrades.add(payload);
      }
      return { ...state, expandedTrades: newExpandedTrades };
    case 'TOGGLE_SCOPE_SELECTION':
      const newSelectedScopes = { ...state.selectedScopes };
      if (!newSelectedScopes[payload.tradeId]) {
        newSelectedScopes[payload.tradeId] = new Set();
      }
      const tradeScopes = new Set(newSelectedScopes[payload.tradeId]);
      if (tradeScopes.has(payload.scopeId)) {
        tradeScopes.delete(payload.scopeId);
      } else {
        tradeScopes.add(payload.scopeId);
      }
      newSelectedScopes[payload.tradeId] = tradeScopes;
      return { ...state, selectedScopes: newSelectedScopes };
    case 'SET_SCOPE_ITEM_CLICK':
      return {
        ...state,
        currentDetailItem: payload.scope,
        currentImages: payload.scope.image_urls || [],
        currentImageIndex: 0
      };
    case 'NEXT_IMAGE':
      return {
        ...state,
        currentImageIndex: (state.currentImageIndex + 1) % state.currentImages.length
      };
    case 'PREVIOUS_IMAGE':
      return {
        ...state,
        currentImageIndex: (state.currentImageIndex - 1 + state.currentImages.length) % state.currentImages.length
      };
    default:
      return state;
  }
};

const store = set => ({
  ...ROSETTA_INITIAL_STATE,
  dispatch: args => set(state => reducer(state, args))
});

export const useRosettaStore = create(devtools(store));
